import React from 'react';
import { Link } from '@gumtree/ui-library';
import { NameValuePair } from '@gumtree/shared/src/model/generic.model';
import HintTooltip from '@gumtree/ui-library/src/hint-tooltip/hint-tooltip';
import { MessageButtons, MessageButtonsProps } from './message-buttons';
import {
    MessageErrors,
    PhoneErrors,
    MessageContainer,
    PhoneInput,
    SendMessageTip,
} from './message.style';
import * as util from './util';
import Textarea from './textarea';

const Message: React.FC<MessageProps> = ({
    providePhone,
    replyPhone,
    advertId,
    errors,
    sendState,
    defaultMessage = '',
    defaultPhoneNumber = '',
    textareaAutoResize,
    isCurrentUserSeller,
    onSendMessageClick,
    onCallClick,
    className,
    messageUseStore,
}) => {
    const { savedPhone, updatePhone, validatePhone, onPhoneInputBlur, isPhoneValid } = util.useStorePhoneNumber(util.phoneRegexComplex, defaultPhoneNumber);
    const { savedMessage, updateMessage, isInitialized } = util.useStoreMessage(advertId, { placeholder: defaultMessage, useStore: messageUseStore });
    const hasError = !!errors?.some(({ value }) => value);

    const validate = () => {
        return validatePhone() && !!savedMessage;
    }

    const handleSendMessageClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (!validate()) return;
        onSendMessageClick(e, {
            message: savedMessage || '',
            phoneNumber: savedPhone || '',
        });
    };

    return (
        <MessageContainer
            className={className}
            hasMessageError={hasError}
            hasPhoneError={!isPhoneValid}
        >
            <fieldset name="provide-message">
                <Textarea
                    textareaAutoResize={!!textareaAutoResize}
                    disabled={!!isCurrentUserSeller}
                    value={savedMessage || ''}
                    onChange={updateMessage}
                />
            </fieldset>
            {providePhone && (
                <fieldset name="provide-phone">
                    <PhoneInput
                        type="tel"
                        error={!isPhoneValid}
                        name="form-phone"
                        id="form-phone"
                        placeholder="Enter your phone number"
                        onChange={updatePhone}
                        onBlur={onPhoneInputBlur}
                        value={savedPhone || ''}
                    />
                </fieldset>
            )}
            {!isPhoneValid && (
                <PhoneErrors>
                    <li>Invalid phone number</li>
                </PhoneErrors>
            )}
            {hasError && (
                <MessageErrors>
                    {errors?.map((formError) => (
                        <li key={formError.name}>{formError.value}</li>
                    ))}
                </MessageErrors>
            )}
            <SendMessageTip>
                By clicking 'Send message', you agree for us to share your details with the
                dealership and MOTORS.{' '}
                <HintTooltip
                    content={
                        <>
                            Gumtree reserves the right to monitor conversations sent through our
                            servers to protect you from fraud, spam or suspicious behaviour. For
                            more information about what we do with your personal data, see our{' '}
                            <Link href="https://www.gumtree.com/privacy_policy" noFollow>
                                Privacy Notice
                            </Link>
                            .
                        </>
                    }
                    triggerColor="grey"
                    closeBtnColor="lightGrey"
                    theme="white"
                    elName="motors-co-uk"
                    hasCloseBtn
                    align="right"
                    hintDirection="top"
                />
            </SendMessageTip>
            <MessageButtons
                replyPhone={!!replyPhone}
                callDisabled={!!isCurrentUserSeller}
                messageDisabled={!isInitialized || !isPhoneValid || Boolean(!savedMessage || isCurrentUserSeller)}
                onSendMessageClick={handleSendMessageClick}
                onCallClick={onCallClick}
                sendState={sendState}
            />
        </MessageContainer>
    );
};

export type MessageParams = {
    message: string;
    phoneNumber?: string;
}

export interface MessageProps {
    className?: string;
    defaultPhoneNumber?: string;
    defaultMessage?: string;
    advertId: string;
    sendState: MessageButtonsProps['sendState'];
    errors?: NameValuePair[];
    providePhone?: boolean;
    replyPhone?: boolean;
    isCurrentUserSeller?: boolean;
    textareaAutoResize?: boolean;
    messageUseStore?: boolean;
    onSendMessageClick: (e: React.MouseEvent<HTMLButtonElement>, params: MessageParams) => void;
    onCallClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}

export default Message;
